import { normalizeForMatch } from './routing.ts';

/**
 * Reads what a customer wants to buy from their own words, so nobody types an order the customer already wrote:
 * "Is Versace Eros still QAR 289? I want to order 2" -> 2 × Versace Eros EDP 100 ml, quoted QAR 289.
 *
 * Pure and deterministic (English and Gulf Arabic, Latin and Arabic-Indic digits). The suggestion panel, the
 * assistant's rules and the assistant's model tools all use it, so a price never comes from anywhere but the
 * catalogue.
 */
export interface CatalogueItem {
  id: string;
  sku: string;
  brand: string;
  name: string;
  nameAr?: string | null;
  sizeMl?: number | null;
  category?: string | null;
  priceMinor: number;
  salePriceMinor?: number | null;
  stock: number | null;
}

export interface ExtractedLine {
  product: CatalogueItem;
  quantity: number;
  /** The customer stated the quantity (otherwise 1 is assumed). */
  quantityStated: boolean;
  /** Price the customer quoted for it, in minor units, when they did. */
  quotedMinor: number | null;
  /** 0..1: how sure the match is. */
  score: number;
  /** Other products the words could mean (sizes, gift sets), best first. */
  alternatives: CatalogueItem[];
}

export interface ExtractedOrder {
  lines: ExtractedLine[];
  /** The message reads as wanting to buy (not just asking). */
  wantsToBuy: boolean;
}

export const productLabelOf = (p: Pick<CatalogueItem, 'brand' | 'name' | 'sizeMl'>) =>
  `${p.brand} ${p.name}${p.sizeMl ? ` ${p.sizeMl} ml` : ''}`;

export const shelfPriceMinor = (p: Pick<CatalogueItem, 'priceMinor' | 'salePriceMinor'>) =>
  p.salePriceMinor ?? p.priceMinor;

// ----------------------------------------------------------------------------------------------- normalising

const ARABIC_DIGITS = /[٠-٩۰-۹]/g;
const digitOf = (c: string) => String((c.charCodeAt(0) - (c >= '۰' ? 0x6f0 : 0x660)) % 10);

export function normaliseText(s: string): string {
  return normalizeForMatch(s.replace(ARABIC_DIGITS, digitOf))
    .replace(/(\d)\s*(ml|مل)\b/g, '$1ml')
    .replace(/[^\p{L}\p{N}.\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

const tokens = (s: string) => normaliseText(s).split(' ').filter(Boolean);

/** Words that say nothing about which product (concentrations, "perfume", articles, Arabic equivalents). */
const GENERIC = new Set(
  [
    'eau',
    'de',
    'du',
    'la',
    'le',
    'parfum',
    'perfume',
    'toilette',
    'cologne',
    'edp',
    'edt',
    'extrait',
    'intense',
    'the',
    'for',
    'men',
    'women',
    'him',
    'her',
    'pour',
    'homme',
    'femme',
    'by',
    'and',
    'of',
    'set',
    'gift',
    'pcs',
    'ml',
    // Short words that appear in names and in everyday sentences ("Al Wakra", "the", "new").
    'al',
    'el',
    'bin',
    'abu',
    'new',
    'with',
    'in',
    'on',
    'to',
    'no',
    'عطر',
    'او',
    'دو',
    'بارفان',
    'تواليت',
    'لو',
    'دي',
    'للرجال',
    'للنساء',
    'مجموعه',
    'هديه',
  ].map(normalizeForMatch),
);

const SET_WORDS = ['set', 'gift set', 'مجموعه', 'طقم', 'بوكس', 'box'].map(normalizeForMatch);
const CONCENTRATION: Record<string, string[]> = {
  edp: ['edp', 'eau de parfum', 'parfum', 'بارفان'],
  edt: ['edt', 'eau de toilette', 'toilette', 'تواليت'],
};

// ---------------------------------------------------------------------------------------------- quantities

const NUMBER_WORDS: Record<string, number> = {
  one: 1,
  single: 1,
  a: 1,
  an: 1,
  two: 2,
  pair: 2,
  couple: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  [normalizeForMatch('واحد')]: 1,
  [normalizeForMatch('وحده')]: 1,
  [normalizeForMatch('حبه')]: 1,
  [normalizeForMatch('اثنين')]: 2,
  [normalizeForMatch('ثنتين')]: 2,
  [normalizeForMatch('اثنتين')]: 2,
  [normalizeForMatch('حبتين')]: 2,
  [normalizeForMatch('علبتين')]: 2,
  [normalizeForMatch('قطعتين')]: 2,
  [normalizeForMatch('زجاجتين')]: 2,
  [normalizeForMatch('ثلاث')]: 3,
  [normalizeForMatch('ثلاثه')]: 3,
  [normalizeForMatch('اربع')]: 4,
  [normalizeForMatch('اربعه')]: 4,
  [normalizeForMatch('خمس')]: 5,
  [normalizeForMatch('خمسه')]: 5,
};
const UNIT_WORDS = new Set(
  [
    'x',
    'pcs',
    'pieces',
    'piece',
    'bottles',
    'bottle',
    'units',
    'qty',
    'حبات',
    'حبه',
    'علب',
    'قطع',
    'زجاجات',
  ].map(normalizeForMatch),
);
const CURRENCY = new Set(
  [
    'qar',
    'qr',
    'riyal',
    'riyals',
    'rial',
    'aed',
    'dhs',
    'sar',
    'kwd',
    'bhd',
    'omr',
    'inr',
    'rs',
    'ريال',
    'درهم',
    'دينار',
    'ر.ق',
    'رق',
    'روبيه',
  ].map(normalizeForMatch),
);
const ORDER_WORDS = [
  'order',
  'buy',
  'want',
  'need',
  'take',
  'send',
  'reserve',
  'book',
  'get',
  'purchase',
  'add',
].map(normalizeForMatch);
const ORDER_WORDS_AR = [
  'اطلب',
  'ابي',
  'ابغي',
  'ابغى',
  'اريد',
  'ودي',
  'احجز',
  'ارسل',
  'اشتري',
  'خذ',
  'بغيت',
  'طلب',
].map(normalizeForMatch);

const NAME_NUMBER = new Set(['no', 'no.', 'number', 'num', 'nr', 'رقم'].map(normalizeForMatch));

/** A bare number is a price when a currency is beside it, or when it is too big to be a quantity. */
function numberRole(ts: string[], i: number): 'price' | 'size' | 'quantity' | 'other' {
  const t = ts[i]!;
  if (/ml$/.test(t)) return 'size';
  // "No. 5", "number 5", "رقم ٥" are part of a product name.
  if (NAME_NUMBER.has(ts[i - 1] ?? '')) return 'other';
  const v = Number(t);
  if (!Number.isFinite(v)) return 'other';
  if (CURRENCY.has(ts[i - 1] ?? '') || CURRENCY.has(ts[i + 1] ?? '')) return 'price';
  if (t.includes('.') || v > 20) return 'price';
  if (v <= 0) return 'other';
  return 'quantity';
}

function quotedPrices(ts: string[]): number[] {
  const out: number[] = [];
  ts.forEach((t, i) => {
    if (/^\d+(\.\d+)?$/.test(t) && numberRole(ts, i) === 'price') out.push(Number(t));
  });
  return out;
}

/** Quantity stated around a product mention (the clause it is in). */
function quantityIn(ts: string[]): number | null {
  for (let i = 0; i < ts.length; i++) {
    const t = ts[i]!;
    const xn = /^x(\d{1,2})$/.exec(t) ?? /^(\d{1,2})x$/.exec(t);
    if (xn) return Number(xn[1]);
    if (/^\d{1,2}$/.test(t) && numberRole(ts, i) === 'quantity') return Number(t);
    if (t in NUMBER_WORDS && t !== 'a' && t !== 'an') return NUMBER_WORDS[t]!;
    // "a pair", "a couple"
    if ((t === 'a' || t === 'an') && (ts[i + 1] === 'pair' || ts[i + 1] === 'couple')) return 2;
    if (UNIT_WORDS.has(t) && /^\d{1,2}$/.test(ts[i - 1] ?? '')) return Number(ts[i - 1]);
  }
  return null;
}

/** A quantity stated on its own or in a short answer ("2", "I want 2", "make it three", "ابي ٢"); never a price. */
export function statedQuantity(text: string): number | null {
  const q = quantityIn(tokens(text));
  return q !== null && q > 0 && q <= 50 ? q : null;
}

// ------------------------------------------------------------------------------------------------- matching

interface Variant {
  brand: string[];
  distinctive: string[];
}
interface Indexed {
  item: CatalogueItem;
  /** The English name and the Arabic name, each scored on its own (an Arabic name does not dilute an English match). */
  variants: Variant[];
  isSet: boolean;
  concentration: string | null;
}

const isArabic = (s: string) => /[\u0600-\u06FF]/.test(s);

function index(items: CatalogueItem[]): Indexed[] {
  // Arabic brand words: the first word of Arabic names that most products of the same brand share.
  const firstWords = new Map<string, Map<string, number>>();
  for (const i of items) {
    const w = tokens(i.nameAr ?? '')[0];
    if (!w || !isArabic(w)) continue;
    const m = firstWords.get(i.brand) ?? new Map<string, number>();
    m.set(w, (m.get(w) ?? 0) + 1);
    firstWords.set(i.brand, m);
  }
  return items.map((item) => {
    const brand = tokens(item.brand).filter((t) => !GENERIC.has(t));
    const variant = (name: string, brandWords: string[]): Variant => ({
      brand: brandWords,
      distinctive: [
        ...new Set(
          tokens(name).filter(
            (t) => !GENERIC.has(t) && !/^\d/.test(t) && t.length > 1 && !brandWords.includes(t),
          ),
        ),
      ],
    });
    const variants = [variant(item.name, brand)];
    if (item.nameAr) {
      const ar = tokens(item.nameAr);
      // "فيرساتشي إيروس ..." starts with the brand when the name has more words than that.
      const arBrand = ar.length > 1 && isArabic(ar[0]!) ? [ar[0]!] : [];
      const v = variant(item.nameAr, [...brand, ...arBrand]);
      if (v.distinctive.length) variants.push(v);
    }
    const lname = normaliseText(`${item.name} ${item.nameAr ?? ''}`);
    return {
      item,
      variants: variants.filter((v) => v.distinctive.length),
      isSet: item.category === 'gift_set' || SET_WORDS.some((w) => ` ${lname} `.includes(` ${w} `)),
      concentration:
        Object.entries(CONCENTRATION).find(([, ws]) =>
          ws.some((w) => ` ${lname} `.includes(` ${normaliseText(w)} `)),
        )?.[0] ?? null,
    };
  });
}

/** Arabic words carry prefixes (و، ب، ال): "والايروس" still means "ايروس". */
const tokenHas = (ts: string[], key: string) =>
  ts.some(
    (t) =>
      t === key ||
      (isArabic(key) &&
        t.length > key.length &&
        t.endsWith(key) &&
        /^(?:[وفبلك])?(?:ال)?$/u.test(t.slice(0, -key.length))),
  );

function scoreIn(ts: string[], p: Indexed): number {
  let best = 0;
  for (const v of p.variants) {
    const hits = v.distinctive.filter((d) => tokenHas(ts, d)).length;
    if (!hits) continue;
    const brandHit = v.brand.some((b) => tokenHas(ts, b));
    // One word of a long name, without the brand, is too weak to mean that product.
    if (hits === 1 && v.distinctive.length >= 3 && !brandHit) continue;
    // All the distinctive words is a sure match; one word of a long name is a weak one. The brand adds confidence.
    best = Math.max(best, Math.min(1, (hits / v.distinctive.length) * 0.7 + (brandHit ? 0.3 : 0.15)));
  }
  return best;
}

/** Splits "2 Eros and 1 Sauvage" into one clause per product mention. */
const clauses = (text: string) =>
  text
    .split(/[,،;؛!?؟\n]+|\.(?=\s|$)/)
    .flatMap((c) => normaliseText(c).split(/\s(?:and|plus|also|with|&|و)\s/))
    .map((c) => c.trim())
    .filter(Boolean);

export function extractOrder(text: string, catalogue: CatalogueItem[]): ExtractedOrder {
  const all = tokens(text);
  const wantsToBuy =
    all.some((t) => ORDER_WORDS.includes(t)) ||
    all.some((t) => ORDER_WORDS_AR.some((w) => t === w || t.endsWith(w))) ||
    quantityIn(all) !== null;
  const idx = index(catalogue.filter((c) => c.name));
  const lines: ExtractedLine[] = [];
  const parts = clauses(text);
  const prices = quotedPrices(all);

  for (let ci = 0; ci < parts.length; ci++) {
    const ts = parts[ci]!.split(' ');
    const scored = idx
      .map((p) => ({ p, s: scoreIn(ts, p) }))
      .filter((x) => x.s >= 0.35)
      .sort((a, b) => b.s - a.s);
    if (!scored.length) continue;
    const top = scored[0]!.s;
    let group = scored.filter((x) => x.s >= top - 0.05).map((x) => x.p);

    // Narrow the family (Eros EDP 100, Eros EDT 50, Eros gift set) by what the customer said.
    const said = (ws: string[]) => ws.some((w) => ` ${parts[ci]} `.includes(` ${normaliseText(w)} `));
    const wantsSet = said(SET_WORDS);
    group = prefer(group, (p) => p.isSet === wantsSet);
    const size = ts.find((t) => /^\d+ml$/.test(t));
    if (size) group = prefer(group, (p) => p.item.sizeMl === Number(size.replace('ml', '')));
    for (const [c, ws] of Object.entries(CONCENTRATION))
      if (said(ws)) group = prefer(group, (p) => p.concentration === c);
    const clausePrices = quotedPrices(ts);
    const quoted = clausePrices[0] ?? (parts.length === 1 || lines.length === 0 ? (prices[0] ?? null) : null);
    if (quoted !== null) group = prefer(group, (p) => Math.abs(shelfPriceMinor(p.item) / 100 - quoted) < 0.5);
    group = prefer(group, (p) => p.item.stock !== 0);
    group.sort((a, b) => Number(b.item.salePriceMinor != null) - Number(a.item.salePriceMinor != null));

    const chosen = group[0]!;
    if (lines.some((l) => l.product.id === chosen.item.id)) continue;
    // The quantity is in this clause, or (one product mentioned) anywhere in the message: "... ? I want to order 2".
    const q =
      quantityIn(ts) ??
      (parts.filter((p) => idx.some((x) => scoreIn(p.split(' '), x) >= 0.35)).length === 1
        ? quantityIn(all)
        : null);
    lines.push({
      product: chosen.item,
      quantity: q ?? 1,
      quantityStated: q !== null,
      quotedMinor: quoted === null ? null : Math.round(quoted * 100),
      score: Math.round(top * 100) / 100,
      alternatives: scored
        .map((x) => x.p.item)
        .filter((i) => i.id !== chosen.item.id)
        .slice(0, 3),
    });
  }
  return { lines, wantsToBuy };
}

/** Keeps the items that pass, unless none does (then the filter says nothing useful). */
function prefer<T>(xs: T[], keep: (x: T) => boolean): T[] {
  const kept = xs.filter(keep);
  return kept.length ? kept : xs;
}
